import { useState, useRef } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";
import { NewTaskDotBackground } from "./canvas/NewTaskDotBackground";
import "./workspace-auth.css";

export default function Signup({ onLogin, variant = "standard" }: { onLogin: () => Promise<void> | void; variant?: "standard" | "promo" }) {
  // v7.46 — /promosignup: same form, but the coupon is open + REQUIRED and
  // there is no trial/card step afterwards (credits are granted on the spot).
  const isPromo = variant === "promo";
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // v7.28 — promo coupon: hidden behind a link so the field isn't in the way
  // for normal signups. Checked live so a promo user learns a bad code here,
  // not after creating an account.
  const [couponOpen, setCouponOpen] = useState(variant === "promo");
  const [couponCode, setCouponCode] = useState("");
  const [couponState, setCouponState] = useState<{ status: "idle" | "checking" | "ok" | "bad"; message?: string; credits?: number }>({ status: "idle" });
  const [, setLocation] = useLocation();
  const submittingRef = useRef(false);

  const checkCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponState({ status: "idle" }); return; }
    setCouponState({ status: "checking" });
    try {
      const res = await fetch("/api/auth/validate-coupon", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data?.valid) {
        setCouponState({ status: "ok", credits: data.credits });
      } else {
        setCouponState({ status: "bad", message: data?.message || "That coupon code isn't valid." });
      }
    } catch {
      setCouponState({ status: "bad", message: "Couldn't check that code — try again." });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError("");

    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (isPromo && !couponCode.trim()) {
      setError("A promo code is required to sign up on this page.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim(), password, name: name.trim(),
          // Send whatever they typed — the server re-validates and a bad
          // code never fails signup. Gating on couponState would silently
          // drop a valid code when the async check hadn't resolved yet.
          ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
          ...(isPromo ? { promoSignup: true } : {}),
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || "Signup failed");
        return;
      }

      // Account created + session set. Wait for the auth-context refresh
      // to land BEFORE navigating, otherwise the paywall route sees `user`
      // as still null and bounces us back to /login.
      // v7.28 — a coupon can validate at type-time and still fail at register
      // (deactivated, last slot taken). Say so rather than letting them find
      // out by never seeing the credits.
      const created = await res.json().catch(() => null);
      if (couponCode.trim() && created?.coupon && created.coupon.applied === false) {
        setError("Your account was created, but that coupon couldn't be applied — contact support before starting your trial.");
        setLoading(false);
        submittingRef.current = false;
        await onLogin();
        return;
      }

      await onLogin();
      // Promo users already hold credits and a subscription row, so the plan
      // picker would just bounce them — drop them straight into the app.
      setLocation(isPromo ? "/" : "/billing/select-plan");
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <main className="workspace-auth workspace-auth--signup" data-testid="signup-page">
      <NewTaskDotBackground />
      <div className="workspace-auth__shell">
        <div className="workspace-auth__logo" data-testid="text-logo">
          <img src="/admoji-logo.png" alt="Admoji" width={128} />
        </div>
        <section className="workspace-auth__card" aria-labelledby="signup-title" data-ripple-block>
          <header className="workspace-auth__heading">
            <p className="workspace-auth__eyebrow">YOUR CREATIVE WORKSPACE</p>
            <h1 id="signup-title">{isPromo ? "Redeem your invite." : "Create your account."}</h1>
            <p className="workspace-auth__intro" data-testid="signup-trial-line">
              {isPromo ? "Enter your promo code — no card needed" : "Try free for 7 days — 3,000 free credits"}
            </p>
          </header>

          <form onSubmit={handleSubmit} className="workspace-auth__form" aria-busy={loading}>
            {error && (
              <div className="workspace-auth__error" id="signup-error" role="alert" data-testid="text-signup-error">{error}</div>
            )}
            <div className="workspace-auth__field">
              <label htmlFor="name">Name</label>
              <input
                id="name" name="name" type="text" value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name" autoComplete="name" autoFocus
                aria-required="true" aria-describedby={error ? "signup-error" : undefined}
                data-testid="input-name"
              />
            </div>
            <div className="workspace-auth__field">
              <label htmlFor="email">Email address</label>
              <input
                id="email" name="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" autoComplete="email" autoCapitalize="none" spellCheck={false}
                aria-required="true" aria-describedby={error ? "signup-error" : undefined}
                data-testid="input-email"
              />
            </div>
            <div className="workspace-auth__field">
              <label htmlFor="password">Password</label>
              <div className="workspace-auth__password">
                <input
                  id="password" name="password" type={showPassword ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters" autoComplete="new-password"
                  aria-required="true" aria-describedby={error ? "signup-error" : undefined}
                  data-testid="input-password"
                />
                <button type="button" className="workspace-auth__password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}
                  onMouseDown={event => event.preventDefault()} onClick={() => setShowPassword(value => !value)}
                  data-testid="signup-password-toggle">
                  {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            {!couponOpen ? (
              <button type="button" className="workspace-auth__coupon-toggle" data-testid="signup-coupon-toggle"
                aria-expanded={false} onClick={() => setCouponOpen(true)}>Have a coupon?</button>
            ) : (
              <div className="workspace-auth__field">
                <label htmlFor="coupon">{isPromo ? "Promo code (required)" : "Coupon code"}</label>
                <div className="workspace-auth__coupon-row">
                  <input
                    id="coupon" name="couponCode" type="text" value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState({ status: "idle" }); }}
                    // Rechecking a valid unchanged code removes its status and
                    // can move Create account between pointerdown and click.
                    // Edits reset status to idle; registration validates again.
                    onBlur={() => { if (couponState.status !== "ok") void checkCoupon(); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void checkCoupon(); } }}
                    placeholder="PROMO250" autoCapitalize="characters" spellCheck={false}
                    aria-required={isPromo} aria-describedby="signup-coupon-status" data-testid="input-coupon"
                  />
                  <button type="button" className="workspace-auth__secondary" data-testid="signup-coupon-apply"
                    onClick={() => void checkCoupon()} disabled={!couponCode.trim() || couponState.status === "checking"}>
                    {couponState.status === "checking" ? "Checking…" : "Apply"}
                  </button>
                </div>
                <div id="signup-coupon-status" className="workspace-auth__coupon-status" aria-live="polite">
                  {couponState.status === "ok" && (
                    <p data-testid="coupon-ok" className="workspace-auth__success">
                      Coupon applied — {couponState.credits?.toLocaleString()} free credits land when your trial starts.
                    </p>
                  )}
                  {couponState.status === "bad" && <p data-testid="coupon-bad" className="workspace-auth__coupon-error">{couponState.message}</p>}
                </div>
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="button-signup" className="workspace-auth__submit">
              {loading ? <><Loader2 className="workspace-auth__spinner" aria-hidden="true" />Creating account...</> : <>Create account<ArrowRight aria-hidden="true" /></>}
            </button>

            {!isPromo && import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <div className="workspace-auth__google">
                <div className="workspace-auth__divider"><span>or continue with</span></div>
                <GoogleSignInButton
                  theme="outline"
                  extraBody={couponCode.trim() ? { couponCode: couponCode.trim() } : undefined}
                  onSuccess={async () => { await onLogin(); setLocation("/billing/select-plan"); }}
                  onError={setError}
                />
              </div>
            )}
            <p className="workspace-auth__terms">
              By creating an account you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
            </p>
          </form>
          <p className="workspace-auth__signup">Already have an account? <Link href="/login">Sign in<ArrowRight aria-hidden="true" /></Link></p>
        </section>
        <p className="workspace-auth__footer">Your next great ad starts here.</p>
      </div>
    </main>
  );
}
